// src/components/avatar.tsx
import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import React, { forwardRef } from 'react';
import { TouchTarget } from './buttons/button';
import { Link } from './link';
import { semanticColors } from '../theme';

type AvatarProps = {
  src?: string | null;
  square?: boolean;
  initials?: string;
  alt?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
};

export function Avatar({
  src = null,
  square = false,
  initials,
  alt = '',
  className,
  size = 'md',
  ...props
}: AvatarProps & React.ComponentPropsWithoutRef<'span'>) {
  const sizeClasses = {
    sm: 'w-8 h-8 text-[24px]',
    md: 'w-12 h-12 text-[36px]',
    lg: 'w-16 h-16 text-[48px]',
  };

  return (
    <span
      data-slot="avatar"
      {...props}
      className={clsx(
        className,
        sizeClasses[size],
        'inline-grid shrink-0 align-middle [--avatar-radius:20%] [--ring-opacity:20%] *:col-start-1 *:row-start-1',
        'outline outline-1 -outline-offset-1',
        square ? 'rounded-[--avatar-radius] *:rounded-[--avatar-radius]' : 'rounded-full *:rounded-full'
      )}
      style={{
        outlineColor: semanticColors.borderMedium,
        backgroundColor: semanticColors.badge.info.background,
        color: semanticColors.badge.info.text,
      }}
    >
      {initials && (
        <svg
          className={clsx(
            'select-none fill-current p-[5%] font-medium uppercase',
            sizeClasses[size]
          )}
          viewBox="0 0 100 100"
          aria-hidden={alt ? undefined : 'true'}
        >
          {alt && <title>{alt}</title>}
          <text x="50%" y="50%" alignmentBaseline="middle" dominantBaseline="middle" textAnchor="middle" dy=".125em">
            {initials}
          </text>
        </svg>
      )}
      {src && <img className="size-full" src={src} alt={alt} />}
    </span>
  );
}

export const AvatarButton = forwardRef(function AvatarButton(
  {
    src,
    square = false,
    initials,
    alt,
    className,
    size = 'md', // Added size prop
    ...props
  }: AvatarProps &
    (Omit<Headless.ButtonProps, 'className'> | Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>),
  ref: React.ForwardedRef<HTMLElement>
) {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
  };

  const classes = clsx(
    className,
    sizeClasses[size], // Apply size-specific classes
    square ? 'rounded-[20%]' : 'rounded-full',
    'relative inline-grid focus:outline-none data-[focus]:outline data-[focus]:outline-2 data-[focus]:outline-offset-2 data-[focus]:outline-blue-500'
  );

  return 'href' in props ? (
    <Link {...props} className={classes} ref={ref as React.ForwardedRef<HTMLAnchorElement>}>
      <TouchTarget>
        <Avatar src={src} square={square} initials={initials} alt={alt} size={size} />
      </TouchTarget>
    </Link>
  ) : (
    <Headless.Button {...props} className={classes} ref={ref}>
      <TouchTarget>
        <Avatar src={src} square={square} initials={initials} alt={alt} size={size} />
      </TouchTarget>
    </Headless.Button>
  );
});